import { HTMLAttributes, type JSX } from 'react';
import { type DropzoneOptions, useDropzone } from 'react-dropzone';

import { CloudUploadIcon } from '../../svg/cloud-upload-icon';
import { cn } from '../../utils/cn';
import { CONVERT_UNIT, convertBytes } from '../../utils/convert-bytes';

const DEFAULT_OPTIONS = {
  maxFiles: 10,
  maxSize: 5_000_000,
  multiple: true,
};
const DISABLE_DROP = { onDrop: (event: any) => event.preventDefault() };

export interface DropzoneProps extends Pick<HTMLAttributes<HTMLDivElement>, 'className'> {
  description?: string;
  icon?: JSX.Element;
  label?: string | JSX.Element;
  options?: DropzoneOptions;
}

/**
 * @param options **optional** an object with all the callbacks to handle file upload and more. See: https://react-dropzone.js.org/#section-components
 * for `options/accept` see https://www.iana.org/assignments/media-types/media-types.xhtml#image
 *
 * Example of onDrop callback
 * ```
 * const onDrop = useCallback(
 *  (
 *    acceptedFiles: File[],
 *    rejectedFiles: FileRejection[],
 *    _event: DropEvent,
 *  ) => {},
 *  [],
 * );
 * ```
 */
export function Dropzone({
  className,
  description,
  icon = <CloudUploadIcon className="size-6" />,
  label,
  options,
}: DropzoneProps) {
  const newOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone(newOptions);

  return (
    <div
      className={cn(
        'flex min-h-40 cursor-pointer items-center justify-center rounded-3xl border-2 border-dotted border-input bg-transparent text-center',
        {
          'cursor-not-allowed text-muted-foreground': newOptions?.disabled,
          'border-success bg-success/5 hover:bg-success/5': isDragActive,
        },
        className,
      )}
      {...getRootProps()}
      {...(newOptions?.disabled ? DISABLE_DROP : {})}
    >
      <input {...getInputProps()} />
      <div className="flex flex-col items-center gap-3">
        <div
          className={cn('text-success', {
            'text-muted-foreground': newOptions?.disabled,
          })}
        >
          {icon}
        </div>
        <div className="space-y-1">
          <div className="text-base">
            <p>
              <span
                className={cn('mr-1 text-primary', {
                  'text-muted-foreground': newOptions?.disabled,
                })}
              >
                Selecciona
              </span>
              {label ?? 'un archivo a subir'}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">o arrástralo y suéltalo aquí</p>
        </div>
        <div className="text-xs text-muted-foreground">
          {description ? <p>{description}</p> : null}
          <p>
            {newOptions.maxSize === Infinity ? null : (
              <>
                Max{' '}
                {convertBytes({
                  bytes: newOptions?.maxSize,
                  to: CONVERT_UNIT.megabyte,
                })}{' '}
                {newOptions?.multiple ? 'por archivo.' : ''}
              </>
            )}

            {newOptions?.multiple ? (
              <span className="ml-1">
                {newOptions.maxFiles === 0 ? (
                  ''
                ) : (
                  <>
                    Max {newOptions?.maxFiles} archivo
                    {newOptions?.maxFiles === 1 ? '' : 's'}.
                  </>
                )}
              </span>
            ) : null}
          </p>
        </div>
      </div>
    </div>
  );
}
